/**
 * "Where did you hear about us?" on registration (WP1.9).
 *
 * The source type is required. Named sources (institutions, events,
 * partners) are data managed by super moderators; when none fits — or the
 * type is "Other" — the free-text detail is required instead.
 */
import i18n from "../i18n";

export const LEAD_SOURCE_OTHER = "other";
const LEAD_SOURCE_DETAIL_MAX = 255;

export interface LeadSourceValue {
  type: string;
  /** Selected named source id as a string; "" = none / "Other — please specify". */
  sourceId: string;
  detail: string;
}

export type LeadSourceErrors = Partial<Record<"leadSourceType" | "leadSourceId" | "leadSourceDetail", string>>;

export const LEAD_SOURCE_FIELD_KEYS: Array<keyof LeadSourceErrors> = [
  "leadSourceType",
  "leadSourceId",
  "leadSourceDetail",
];

export const EMPTY_LEAD_SOURCE: LeadSourceValue = { type: "", sourceId: "", detail: "" };

/** True when the free-text box is needed (Other, or no named source picked). */
export function needsLeadSourceDetail(value: LeadSourceValue): boolean {
  return value.type === LEAD_SOURCE_OTHER || !value.sourceId;
}

export function validateLeadSource(value: LeadSourceValue): LeadSourceErrors {
  const errors: LeadSourceErrors = {};
  if (!value.type) {
    errors.leadSourceType = i18n.t("domain:leadSource.errors.typeRequired");
    return errors;
  }
  if (needsLeadSourceDetail(value) && !value.detail.trim()) {
    errors.leadSourceDetail = i18n.t("domain:leadSource.errors.detailRequired");
  }
  if (value.detail.length > LEAD_SOURCE_DETAIL_MAX) {
    errors.leadSourceDetail = i18n.t("domain:leadSource.errors.detailTooLong", { max: LEAD_SOURCE_DETAIL_MAX });
  }
  return errors;
}

export function leadSourcePayload(value: LeadSourceValue): {
  leadSourceType: string;
  leadSourceId: number | null;
  leadSourceDetail: string;
} {
  const named = value.type !== LEAD_SOURCE_OTHER && value.sourceId ? Number(value.sourceId) : null;
  return {
    leadSourceType: value.type,
    leadSourceId: named,
    leadSourceDetail: named ? "" : value.detail.trim(),
  };
}

export function pickLeadSourceErrors(fieldErrors: Record<string, string | undefined>): LeadSourceErrors {
  const out: LeadSourceErrors = {};
  for (const key of LEAD_SOURCE_FIELD_KEYS) {
    if (fieldErrors[key]) out[key] = fieldErrors[key];
  }
  return out;
}
